import type { ChannelClient } from '@hibro/platform-core';
import type {
  ConfigChange,
  ConfigInspect,
  ConfigValues,
  IAppLinkService,
  IConfigurationService,
  ILauncherService,
  InstalledModuleInfo,
  IPermissionService,
  ISearchRecentService,
  ModuleFacets,
  ResolvedSetting,
} from '@hibro/shared';
import {
  CONFIG_CHANGED_EVENT,
  CONTRIBUTIONS_CHANGED_EVENT,
  describeModuleFacets,
  PLATFORM_MODULE_ID,
  SCOPE_USER,
} from '@hibro/shared';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hibro/ui-client';
import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useT, useTranslator } from '../../i18n/i18n';
import { AppLinksSection } from '../app-links-section';
import { type LogBridge, LogsSection } from '../logs-view';
import { PermissionsSection } from '../permissions-panel';
import { RailLayoutSection } from '../rail-layout-section';

/**
 * Bridge to the main-process bundle installer (架构方案 §5.4.1, 见 12).
 * Injected by the app shell (so platform-renderer stays free of the preload
 * `window.hibroBundle` global), mirroring `iframeBridge`/`externalBridge`.
 */
export interface BundleInstallBridge {
  install(source: {
    url: string;
    sha256: string;
  }): Promise<{ manifest: { id: string; version: string } }>;
  /**
   * Whether this runtime has network-install mechanics. The settings form
   * hides the url+sha256 fields when this is false, rather than leaving a
   * form that can only fail.
   */
  canInstall?(): Promise<boolean>;
  /**
   * Whether this runtime can sideload from a local `.tgz` or directory.
   * Desktop opens a native dialog in the main process; the browser picks
   * on the page (gesture) and then hands the bytes to the host. False
   * hides the buttons rather than leaving a form that can only fail.
   */
  canInstallLocal?(): Promise<boolean>;
  /**
   * Local-resource (sideload) install (见 12): opens a native picker for a
   * local `.tgz` archive (`'archive'`) or an unpacked module directory
   * (`'dir'`). Resolves with the installed bundle, or null if cancelled.
   */
  installLocal?(
    kind: 'archive' | 'dir',
  ): Promise<{ manifest: { id: string; version: string } } | null>;
  /**
   * List the dynamically-installed (downloaded) modules (见 12), driving the
   * module-manager list. When present, the installed-modules section renders.
   */
  listInstalled?(): Promise<InstalledModuleInfo[]>;
  /**
   * Uninstall a downloaded module by id (见 12): removes its cache + runtime
   * registration; `purgeData` also wipes its persisted data. Resolves with the
   * removed cache dir names.
   */
  uninstall?(
    moduleId: string,
    options?: { purgeData?: boolean },
  ): Promise<{ removed: string[] }>;
}

/** A snapshot of the main-process developer-tools state (见 11 调试). */
export interface DevtoolsSnapshot {
  debug: {
    enabled: boolean;
    port: number;
    token: string;
    forced: boolean;
    /** Last start failure (e.g. port in use), when the server could not bind. */
    error?: string;
  };
  /**
   * Linked (load-unpacked) dev modules, each with its derived execution facts
   * for display (see `describeModuleFacets`).
   */
  linked: { id: string; dir: string; facets: ModuleFacets }[];
  /** Runtime diagnostics toggles: console mirror + HTTP tracing (见 15). */
  diagnostics: {
    mirrorConsole: boolean;
    traceHttp: boolean;
  };
}

/**
 * Bridge to the main-process developer tools (见 11 调试): runtime SDK debug
 * toggle + linked (load-unpacked) module management. Injected by the app shell
 * (so platform-renderer stays free of the preload `window.hibroDevtools` global),
 * mirroring `bundleBridge`. When present, the settings Developer section renders.
 */
export interface DevtoolsBridge {
  getState(): Promise<DevtoolsSnapshot>;
  setDebug(input: {
    enabled: boolean;
    port?: number;
    token?: string;
  }): Promise<DevtoolsSnapshot>;
  /** Toggle runtime diagnostics: console mirror + HTTP tracing (见 15). */
  setDiagnostics(input: {
    mirrorConsole?: boolean;
    traceHttp?: boolean;
  }): Promise<DevtoolsSnapshot>;
  /** One-shot「Open DevTools」action: open DevTools on every window (见 15). */
  openDevtools(): Promise<void>;
  /** Open a native directory picker to link a local module dir. */
  link(): Promise<DevtoolsSnapshot>;
  unlink(dir: string): Promise<DevtoolsSnapshot>;
  /** Re-read every linked manifest + re-register (load-unpacked "Reload"). */
  reload(): Promise<DevtoolsSnapshot>;
}

/** A linked (load-unpacked) dev module as the browser runtime sees it (见 11 调试). */
export interface LinkedModuleInfo {
  /** Manifest id, which is also the key its copy is stored under. */
  id: string;
  /** Picked directory name, for display only — a browser exposes no path. */
  dir: string;
  /**
   * The browser has forgotten its grant for the picked directory, so Reload
   * will prompt. Surfaced so the row can say why rather than letting the click
   * look like it did nothing.
   */
  needsPermission?: boolean;
  /**
   * Why this copy will not become a live module on the current host (e.g.
   * the manifest declares no `web` runtime). The page can copy anything;
   * registration is the host's, and a silent skip looks like "link worked
   * but the rail never appeared".
   */
  warning?: string;
}

/**
 * Bridge to the browser runtime's load-unpacked dev modules (见 11 调试 / 18).
 *
 * Deliberately **not** part of {@link DevtoolsBridge}, even though the desktop
 * puts the same three verbs there: that bridge also carries `setDebug` /
 * `openDevtools` / `setDiagnostics`, which are Electron-only, so sharing it
 * would force one runtime to stub what it cannot do. Two smaller seams, each
 * injected by the runtime that has it, keeps "not injected means not offered"
 * true for both.
 *
 * Every verb is gesture-bound on this runtime: picking a directory opens a
 * native dialog, and re-reading one may have to re-request permission, neither
 * of which is allowed outside a click.
 */
export interface LinkedModulesBridge {
  list(): Promise<LinkedModuleInfo[]>;
  /** Pick a directory and copy it in. Resolves undefined if the user cancelled. */
  link(): Promise<LinkedModuleInfo | undefined>;
  /** Re-read the remembered directory (the "Reload" of load-unpacked). */
  reload(moduleId: string): Promise<void>;
  unlink(moduleId: string): Promise<void>;
}

/**
 * Bridge to the runtime's OS-notification permission (见 18 / §5.15), injected
 * by the app shell like every other capability here.
 *
 * Only the browser runtime has one. A desktop notification is raised by the main
 * process and needs no page grant, so Electron injects nothing and this section
 * never renders — the same "not injected means not offered" rule as the rest.
 *
 * It exists at all because the grant **must** happen inside a user gesture:
 * `notifyOS` arrives over RPC from a module, long out of any gesture's call
 * stack, so the page can only read the permission there, never request it. A
 * settings control is the gesture (见 `src/web/main.tsx`'s `showOSNotification`).
 */
export interface OsNotificationBridge {
  /** Current grant state, read synchronously on each render. */
  permission(): NotificationPermission;
  /**
   * Prompt the user; resolves with the resulting state (`default` if dismissed).
   *
   * **Must not reject.** `Notification.requestPermission()` can throw (insecure
   * context, embedding policy), and the implementation owns reporting that — it
   * sits next to the runtime's log sink, whereas this package deliberately holds
   * no logger. Resolve with the current state instead, so the row keeps telling
   * the truth either way.
   */
  request(): Promise<NotificationPermission>;
}

/**
 * Bridge to the OS's "start this app when I sign in" registration (需求 §4.7),
 * injected by the app shell like every other capability here.
 *
 * **Deliberately not a `SettingContribution`**, which is where every other
 * platform-owned switch lives (见 05 / 23). The truth for this one is the OS
 * registration itself: a user can turn it off in Task Manager's Startup tab
 * while the app is not even running, and the installer writes it before the app
 * has ever launched. A copy in the config layer would be a second answer that
 * silently disagrees with the first — the shape `platform-capability-first`
 * 第四条 rules out. So every read goes to the OS, and this section renders what
 * comes back rather than a stored value.
 *
 * Injected only where a registration exists to write, so the section is absent
 * on the web and in an unpackaged desktop tree — "not offered" beats "offered
 * but broken", the same rule {@link OsNotificationBridge} and
 * `UpdateCapabilities` follow.
 */
/**
 * A way out to the OS's own per-app notification switch (需求 §4.4.2), injected
 * by the app shell like every other capability here.
 *
 * **Deliberately not a permission read.** Electron exposes no API for the system
 * notification authorization state: Windows keeps a per-app deny in the registry
 * keyed by AppUserModelID, macOS keeps it in `UNUserNotificationCenter`, and
 * reaching either needs a native addon — while an unsigned macOS build never
 * delivers a notification at all regardless of what any state says. Rather than
 * display an authorization we cannot actually observe, this offers the jump to the
 * place that knows, and the notification driver logs the OS's `failed` event as
 * the one real signal available (见 23 通知权限).
 *
 * The browser's counterpart is {@link OsNotificationBridge}, which *can* read and
 * request — hence two seams rather than one, following the same "not injected
 * means not offered" rule as the rest.
 */
export interface SystemNotificationSettingsBridge {
  /** Open the OS settings page for this app's notifications. */
  open(): Promise<void>;
}

export interface AutoLaunchBridge {
  isEnabled(): Promise<boolean>;
  /**
   * Turn the registration on or off.
   *
   * **May reject**, unlike {@link OsNotificationBridge.request}: writing to the
   * registry can fail for reasons the user can act on, and a switch that flips
   * back with no stated reason is worse than an error line.
   */
  setEnabled(enabled: boolean): Promise<void>;
}

export interface SettingsPanelProps {
  configService: IConfigurationService;
  /** Permission broker proxy — drives the embedded Permissions section (§5.6). */
  permissionService: IPermissionService;
  /** Underlying channel client, used to react to cross-window config changes. */
  client: ChannelClient;
  /** Launcher projection + rail layout (见 21). */
  launcherService: ILauncherService;
  /** App-link registry (见 21). */
  appLinkService: IAppLinkService;
  /** Recent global-search picks (需求 FR-14 §4.2.1); drives the clear button. */
  searchRecentService: ISearchRecentService;
  /** Bundle installer bridge (见 12); when present, shows the install section. */
  bundleBridge?: BundleInstallBridge;
  /** Log sink bridge (见 15); when present, shows the diagnostics section. */
  logBridge?: LogBridge;
  /** Devtools bridge (见 11 调试); when present, shows the Developer section. */
  devtoolsBridge?: DevtoolsBridge;
  /**
   * Load-unpacked dev modules for runtimes that are not Electron (见 18); when
   * present, shows its own Developer section. The desktop's equivalent rides on
   * {@link DevtoolsBridge} instead — see {@link LinkedModulesBridge} for why the
   * two are separate seams rather than one.
   */
  linkedModulesBridge?: LinkedModulesBridge;
  /**
   * OS-notification permission bridge (见 18); when present, shows the section
   * that lets the user grant it. Browser-only — see {@link OsNotificationBridge}.
   */
  osNotificationBridge?: OsNotificationBridge;
  /**
   * Start-with-the-OS bridge (需求 §4.7); when present, shows the General
   * section. Desktop-only — see {@link AutoLaunchBridge}.
   */
  autoLaunchBridge?: AutoLaunchBridge;
  /**
   * Same sections, without the modal shell. Used by the preferences window's
   * developer-options page. The workbench modal leaves this unset.
   */
  embedded?: boolean;
  onClose: () => void;
}

function ModuleInstallSection({
  bridge,
}: {
  bridge: BundleInstallBridge;
}): ReactNode {
  const t = useT();
  const [url, setUrl] = useState('');
  const [sha256, setSha256] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allowRemote, setAllowRemote] = useState(true);
  const [allowLocal, setAllowLocal] = useState(() => !!bridge.installLocal);

  useEffect(() => {
    let cancelled = false;
    if (bridge.canInstall) {
      void bridge.canInstall().then((ok) => {
        if (!cancelled) {
          setAllowRemote(ok);
        }
      });
    }
    if (bridge.canInstallLocal) {
      void bridge.canInstallLocal().then((ok) => {
        if (!cancelled) {
          setAllowLocal(ok);
        }
      });
    } else {
      setAllowLocal(!!bridge.installLocal);
    }
    return () => {
      cancelled = true;
    };
  }, [bridge]);

  const install = useCallback(() => {
    setBusy(true);
    setStatus(null);
    setError(null);
    bridge
      .install({ url: url.trim(), sha256: sha256.trim() })
      .then(({ manifest }) => {
        setStatus(
          t('settings.modules.installed', {
            id: manifest.id,
            version: manifest.version,
          }),
        );
        setUrl('');
        setSha256('');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBusy(false));
  }, [bridge, url, sha256, t]);

  // Local-resource (sideload) install (见 12): a native picker chooses a local
  // `.tgz` or module directory; a cancelled picker resolves null (no-op).
  const installLocal = useCallback(
    (kind: 'archive' | 'dir') => {
      if (!bridge.installLocal) {
        return;
      }
      setBusy(true);
      setStatus(null);
      setError(null);
      bridge
        .installLocal(kind)
        .then((result) => {
          if (result) {
            setStatus(
              t('settings.modules.installed', {
                id: result.manifest.id,
                version: result.manifest.version,
              }),
            );
          }
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => setBusy(false));
    },
    [bridge, t],
  );

  const canInstall = !busy && url.trim() !== '' && sha256.trim() !== '';

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-xs text-muted-foreground">
        {t('settings.modules.desc')}
      </p>
      {allowRemote ? (
        <>
          <Input
            type="text"
            value={url}
            placeholder={t('settings.modules.url')}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUrl(event.target.value)
            }
          />
          <Input
            type="text"
            value={sha256}
            placeholder={t('settings.modules.sha256')}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSha256(event.target.value)
            }
            className="font-mono"
          />
          <div>
            <Button size="sm" onClick={install} disabled={!canInstall}>
              {busy
                ? t('settings.modules.installing')
                : t('settings.modules.install')}
            </Button>
          </div>
        </>
      ) : null}
      {allowLocal && bridge.installLocal ? (
        <div className="mt-1 flex flex-col gap-1.5 border-t border-(--app-color-border-subtle) pt-2">
          <p className="m-0 text-xs text-muted-foreground">
            {t('settings.modules.localDesc')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => installLocal('archive')}
              disabled={busy}
            >
              {t('settings.modules.installLocalArchive')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => installLocal('dir')}
              disabled={busy}
            >
              {t('settings.modules.installLocalDir')}
            </Button>
          </div>
        </div>
      ) : null}
      {status ? <p className="m-0 text-xs text-success">{status}</p> : null}
      {error ? (
        <p className="m-0 text-xs text-destructive">
          {t('settings.modules.error', { message: error })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Installed-modules section (架构方案 §5.4.1, 见 12). Lists the dynamically
 * downloaded modules and lets the user uninstall one — the inverse of
 * `ModuleInstallSection`. Uninstall opens an inline confirm with a "also delete
 * this module's data" checkbox (per-module storage + permission decisions); on
 * success the host unregisters the module and broadcasts
 * `CONTRIBUTIONS_CHANGED_EVENT`, which both refreshes this list and drops the
 * module's views elsewhere without a restart. The list also re-pulls on that
 * event so a peer window's install/uninstall is reflected here.
 */
function InstalledModulesSection({
  bridge,
  client,
}: {
  bridge: BundleInstallBridge;
  client: ChannelClient;
}): ReactNode {
  const t = useT();
  const [modules, setModules] = useState<InstalledModuleInfo[]>([]);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [purgeData, setPurgeData] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (!bridge.listInstalled) {
      return;
    }
    bridge
      .listInstalled()
      .then(setModules)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [bridge]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reflect installs/uninstalls from any window (见 12): the host
  // broadcasts CONTRIBUTIONS_CHANGED_EVENT (a platform public event) on every
  // runtime module-set change, so re-pull the list.
  useEffect(() => {
    return client.listen(CONTRIBUTIONS_CHANGED_EVENT, () => refresh());
  }, [client, refresh]);

  const uninstall = useCallback(
    (moduleId: string) => {
      if (!bridge.uninstall) {
        return;
      }
      setBusyId(moduleId);
      setError(null);
      bridge
        .uninstall(moduleId, { purgeData })
        .then(() => {
          setConfirmId(null);
          setPurgeData(false);
          refresh();
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => setBusyId(null));
    },
    [bridge, purgeData, refresh],
  );

  if (modules.length === 0) {
    return (
      <p className="m-0 text-xs text-muted-foreground">
        {t('settings.modules.installed.empty')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {modules.map((mod) => {
        const confirming = confirmId === mod.id;
        const busy = busyId === mod.id;
        return (
          <div
            key={mod.id}
            className="flex flex-col gap-1.5 border-b border-(--app-color-border-subtle) py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px]">
                <span className="font-semibold">
                  {mod.displayName ?? mod.id}
                </span>{' '}
                <span className="text-xs text-(--app-color-text-faint)">
                  {mod.id} · v{mod.version} · {describeModuleFacets(mod.facets)}
                </span>
              </span>
              {!confirming ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmId(mod.id)}
                  disabled={busy}
                >
                  {t('settings.modules.installed.uninstall')}
                </Button>
              ) : null}
            </div>
            {confirming ? (
              <div className="flex flex-col gap-1.5 rounded-md bg-muted p-2">
                <span className="text-xs">
                  {t('settings.modules.installed.confirm', { id: mod.id })}
                </span>
                <Label className="flex items-center gap-1.5 text-xs font-normal">
                  <Checkbox
                    checked={purgeData}
                    onCheckedChange={(checked) => setPurgeData(checked)}
                  />
                  {t('settings.modules.installed.purgeData')}
                </Label>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => uninstall(mod.id)}
                    disabled={busy}
                  >
                    {busy
                      ? t('settings.modules.installed.uninstalling')
                      : t('settings.modules.installed.confirmUninstall')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setConfirmId(null);
                      setPurgeData(false);
                    }}
                    disabled={busy}
                  >
                    {t('settings.modules.installed.cancel')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      {error ? (
        <p className="m-0 text-xs text-destructive">
          {t('settings.modules.error', { message: error })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * OS-notification permission section (见 18 / §5.15). Browser-only: it renders
 * only where the shell injected an {@link OsNotificationBridge}, because a
 * desktop notification needs no page grant.
 *
 * The whole point is to be a **user gesture**. Modules reach `notifyOS` over RPC,
 * where `Notification.requestPermission()` would be ignored, so without a control
 * like this one a granted-by-default browser is the only place OS notifications
 * ever appear — and everywhere else they fail silently.
 *
 * `denied` is terminal: the browser will not re-prompt, and only the user can
 * undo it from site settings. Saying so beats a button that does nothing.
 */
function OsNotificationSection({
  bridge,
}: {
  bridge: OsNotificationBridge;
}): ReactNode {
  const t = useT();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    bridge.permission(),
  );
  const [busy, setBusy] = useState(false);

  // `request()` is contractually non-rejecting (see the bridge), so there is no
  // failure branch to handle here — a refusal comes back as a permission value.
  const request = useCallback(() => {
    setBusy(true);
    void bridge
      .request()
      .then(setPermission)
      .finally(() => setBusy(false));
  }, [bridge]);

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-xs text-muted-foreground">
        {t(`settings.osNotifications.${permission}`)}
      </span>
      {permission === 'default' ? (
        <Button variant="outline" size="sm" onClick={request} disabled={busy}>
          {t('settings.osNotifications.allow')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * General section (需求 §4.7): the "start when I sign in" switch.
 *
 * The OS is the source of truth (见 {@link AutoLaunchBridge}), so the checkbox
 * is re-read from it after every write instead of being advanced optimistically:
 * a rejected write must leave the box showing what the OS actually says, not
 * what the click asked for.
 */
function GeneralSection({ bridge }: { bridge: AutoLaunchBridge }): ReactNode {
  const t = useT();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void bridge.isEnabled().then((value) => {
      if (active) {
        setEnabled(value);
      }
    });
    return () => {
      active = false;
    };
  }, [bridge]);

  const toggle = useCallback(
    (next: boolean) => {
      setBusy(true);
      setError(null);
      void bridge
        .setEnabled(next)
        .catch((err: unknown) =>
          setError(err instanceof Error ? err.message : String(err)),
        )
        .then(() => bridge.isEnabled())
        .then(setEnabled)
        .finally(() => setBusy(false));
    },
    [bridge],
  );

  return (
    <div className="flex flex-col gap-1.5 py-2">
      <Label className="flex items-center gap-2 text-[13px] font-normal">
        <Checkbox
          checked={enabled === true}
          disabled={busy || enabled === null}
          onCheckedChange={toggle}
        />
        {t('settings.general.autoLaunch')}
      </Label>
      <p className="m-0 text-xs text-muted-foreground">
        {t('settings.general.autoLaunch.desc')}
      </p>
      {error ? (
        <p className="m-0 text-xs text-destructive">
          {t('settings.general.autoLaunch.error', { message: error })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A titled settings section card (IM reskin): a small uppercase label header,
 * an optional description, and a bordered surface body. Gives the settings page
 * a consistent grouped look matching the workbench panels.
 */
function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="mt-5">
      <h3 className="m-0 mb-0.5 text-[11px] font-bold tracking-[0.06em] uppercase text-muted-foreground">
        {title}
      </h3>
      {description ? (
        <p className="m-0 mb-2 text-xs text-muted-foreground">{description}</p>
      ) : (
        <div className="h-1.5" />
      )}
      <div className="rounded-xl border border-(--app-color-border-subtle) bg-(--app-color-bg-subtle) px-3.5 py-1.5">
        {children}
      </div>
    </section>
  );
}

/**
 * Developer section (见 11 调试): a runtime SDK debug toggle (start/stop the
 * loopback WebSocket server without a restart) and linked (load-unpacked) module
 * management (link a local working dir, Reload to re-read its manifest, Unlink) —
 * the "developer mode + load unpacked + reload" workflow other extension
 * platforms use. Dev-facing, so copy is plain English (not in the shell catalog).
 */
function DeveloperSection({ bridge }: { bridge: DevtoolsBridge }): ReactNode {
  const [snapshot, setSnapshot] = useState<DevtoolsSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [port, setPort] = useState('');

  const apply = useCallback((next: DevtoolsSnapshot) => {
    setSnapshot(next);
    setToken(next.debug.token);
    setPort(String(next.debug.port));
  }, []);

  useEffect(() => {
    bridge
      .getState()
      .then(apply)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [bridge, apply]);

  const run = useCallback(
    (op: () => Promise<DevtoolsSnapshot>) => {
      setBusy(true);
      setError(null);
      op()
        .then(apply)
        .catch((err: unknown) =>
          setError(err instanceof Error ? err.message : String(err)),
        )
        .finally(() => setBusy(false));
    },
    [apply],
  );

  if (!snapshot) {
    return (
      <p className="m-0 text-xs text-muted-foreground">{error ?? 'Loading…'}</p>
    );
  }

  const { debug, linked, diagnostics } = snapshot;
  const wsUrl = `ws://127.0.0.1:${String(debug.port)}/`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label className="flex items-center gap-2 text-[13px] font-normal">
          <Checkbox
            checked={debug.enabled}
            disabled={busy || debug.forced}
            onCheckedChange={(checked) =>
              run(() =>
                bridge.setDebug({
                  enabled: checked,
                  port: Number(port) || undefined,
                  token: token.trim() || undefined,
                }),
              )
            }
          />
          Enable SDK debug server (loopback only)
        </Label>
        <p className="m-0 text-xs text-muted-foreground">
          {debug.forced
            ? 'Forced on by product config (env/argv/product.json); cannot be turned off here.'
            : 'Lets a module developed outside the client reverse-connect over WebSocket. Bound to 127.0.0.1 + token; only enable for development.'}
        </p>
        {debug.error ? (
          <p className="m-0 text-xs text-destructive">
            {debug.error}（换个端口再试）
          </p>
        ) : null}
        {debug.enabled ? (
          <div className="flex flex-col gap-1 font-mono text-xs text-(--app-color-text-faint)">
            <span>server: {wsUrl}</span>
            <span>token: {debug.token}</span>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <Label className="flex items-center gap-2 text-xs font-normal">
            port:
            <Input
              type="number"
              value={port}
              placeholder="9230"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setPort(event.target.value)
              }
              onBlur={() =>
                run(() =>
                  bridge.setDebug({
                    enabled: debug.enabled,
                    port: Number(port) || undefined,
                    token: token.trim() || undefined,
                  }),
                )
              }
              className="h-7 w-22 font-mono"
              disabled={busy}
            />
          </Label>
          <Label className="flex flex-1 items-center gap-2 text-xs font-normal">
            token:
            <Input
              type="text"
              value={token}
              placeholder="debug token"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setToken(event.target.value)
              }
              onBlur={() =>
                run(() =>
                  bridge.setDebug({
                    enabled: debug.enabled,
                    port: Number(port) || undefined,
                    token: token.trim() || undefined,
                  }),
                )
              }
              className="h-7 font-mono"
              disabled={busy}
            />
          </Label>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 border-t border-(--app-color-border-subtle) pt-2">
        <Label className="flex items-center gap-2 text-[13px] font-normal">
          <Checkbox
            checked={diagnostics.mirrorConsole}
            disabled={busy}
            onCheckedChange={(checked) =>
              run(() => bridge.setDiagnostics({ mirrorConsole: checked }))
            }
          />
          Mirror logs to DevTools console
        </Label>
        <p className="m-0 text-xs text-muted-foreground">
          Replays the whole log pipeline (main / ext-host / renderer / modules)
          onto this window&apos;s DevTools console — useful on a packaged build.
          Defaults to the product config; your choice here overrides it.
        </p>
        <Label className="flex items-center gap-2 text-[13px] font-normal">
          <Checkbox
            checked={diagnostics.traceHttp}
            disabled={busy}
            onCheckedChange={(checked) =>
              run(() => bridge.setDiagnostics({ traceHttp: checked }))
            }
          />
          Trace module HTTP requests
        </Label>
        <p className="m-0 text-xs text-muted-foreground">
          Logs every module request in full —{' '}
          <code>method / url / status / durationMs</code> plus request &amp;
          response headers (credential headers such as{' '}
          <code>Authorization</code> are redacted) and bodies (large bodies
          truncated, binary elided). Debug-only: only enable while
          troubleshooting and don&apos;t share these logs. Applies live to
          running modules; defaults to the product config (dev on / packaged
          off) and your choice here overrides it.
        </p>
        <div className="flex items-center gap-2 pt-0.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void bridge.openDevtools();
            }}
            disabled={busy}
          >
            Open DevTools
          </Button>
          <span className="text-xs text-muted-foreground">
            Opens Chromium DevTools (console + Network) on every window,
            including detached ones — no dev build needed. Close it normally and
            click again to reopen.
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 border-t border-(--app-color-border-subtle) pt-2">
        <p className="m-0 text-xs text-muted-foreground">
          Link a local module directory (its <code>module.manifest.json</code>{' '}
          is read in place — no install). Edit the manifest / rebuild and
          Reload; no uninstall/reinstall.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => run(() => bridge.link())}
            disabled={busy}
          >
            Link module directory…
          </Button>
          {linked.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => run(() => bridge.reload())}
              disabled={busy}
            >
              Reload all
            </Button>
          ) : null}
        </div>
        {linked.length === 0 ? (
          <p className="m-0 text-xs text-muted-foreground">
            No linked modules.
          </p>
        ) : (
          linked.map((mod) => (
            <div
              key={mod.dir}
              className="flex items-center justify-between gap-2 border-b border-(--app-color-border-subtle) py-1.5"
            >
              <span className="overflow-hidden text-xs">
                <span className="font-semibold">{mod.id}</span>{' '}
                <span className="text-(--app-color-text-faint)">
                  {describeModuleFacets(mod.facets)}
                </span>
                <br />
                <span className="font-mono text-[11px] text-(--app-color-text-faint)">
                  {mod.dir}
                </span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => run(() => bridge.unlink(mod.dir))}
                disabled={busy}
              >
                Unlink
              </Button>
            </div>
          ))
        )}
      </div>

      {error ? <p className="m-0 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Linked-module section for runtimes whose sideload is not the desktop's
 * load-unpacked (见 11 调试 / 18): the browser, where a directory is reached
 * through a picked handle and copied in rather than mounted by reference.
 *
 * The verbs read the same as the desktop's, but two things differ and the copy
 * says so: Reload has to be per-module (each has its own remembered handle and
 * its own grant), and a rebuilt `dist/` needs that Reload, because nothing is
 * being read live.
 */
function LinkedModulesSection({
  bridge,
}: {
  bridge: LinkedModulesBridge;
}): ReactNode {
  const [linked, setLinked] = useState<LinkedModuleInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    bridge
      .list()
      .then(setLinked)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [bridge]);

  useEffect(refresh, [refresh]);

  const run = useCallback(
    (op: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      op()
        .catch((err: unknown) =>
          setError(err instanceof Error ? err.message : String(err)),
        )
        .finally(() => {
          setBusy(false);
          refresh();
        });
    },
    [refresh],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-xs text-muted-foreground">
        Link a local module directory. Only its delivered shape is copied in (
        <code>module.manifest.json</code> plus <code>assets/</code>,{' '}
        <code>nls/</code> and <code>dist/</code>) — never{' '}
        <code>node_modules/</code>. Rebuild, then Reload to copy again.
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => run(() => bridge.link())}
          disabled={busy}
        >
          Link module directory…
        </Button>
      </div>
      {linked.length === 0 ? (
        <p className="m-0 text-xs text-muted-foreground">No linked modules.</p>
      ) : (
        linked.map((mod) => (
          <div
            key={mod.id}
            className="flex items-center justify-between gap-2 border-b border-(--app-color-border-subtle) py-1.5"
          >
            <span className="overflow-hidden text-xs">
              <span className="font-semibold">{mod.id}</span>
              <br />
              <span className="font-mono text-[11px] text-(--app-color-text-faint)">
                {mod.dir}
                {mod.needsPermission
                  ? ' — access expired; Reload to re-grant'
                  : ''}
              </span>
              {mod.warning ? (
                <>
                  <br />
                  <span className="text-[11px] text-destructive">
                    {mod.warning}
                  </span>
                </>
              ) : null}
            </span>
            <span className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => run(() => bridge.reload(mod.id))}
                disabled={busy}
              >
                Reload
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => run(() => bridge.unlink(mod.id))}
                disabled={busy}
              >
                Unlink
              </Button>
            </span>
          </div>
        ))
      )}
      {error ? <p className="m-0 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Settings UI (架构方案 §5.12). Renders the aggregated settings schema as form
 * controls (one per declared type), writes edits back through the
 * configuration service over RPC, and listens for `CONFIG_CHANGED_EVENT` so an
 * edit made in another window (or by the service) is reflected here too.
 */
export function SettingsPanel({
  configService,
  permissionService,
  client,
  launcherService,
  appLinkService,
  searchRecentService,
  bundleBridge,
  logBridge,
  devtoolsBridge,
  linkedModulesBridge,
  osNotificationBridge,
  autoLaunchBridge,
  embedded = false,
  onClose,
}: SettingsPanelProps): ReactNode {
  const t = useT();
  const [schema, setSchema] = useState<ResolvedSetting[]>([]);
  const [values, setValues] = useState<ConfigValues>({});
  const [inspects, setInspects] = useState<Record<string, ConfigInspect>>({});
  const [error, setError] = useState<string | null>(null);

  const refreshInspect = useCallback(
    (key: string) => {
      configService
        .inspect(key)
        .then((inspect) => {
          setInspects((prev) => ({ ...prev, [key]: inspect }));
        })
        .catch(() => undefined);
    },
    [configService],
  );

  const load = useCallback(() => {
    Promise.all([configService.getSchema(), configService.getAll()])
      .then(([nextSchema, nextValues]) => {
        setSchema(nextSchema);
        setValues(nextValues);
        setError(null);
        for (const setting of nextSchema) {
          refreshInspect(setting.key);
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [configService, refreshInspect]);

  useEffect(() => {
    load();
  }, [load]);

  // Stay consistent when the value changes elsewhere (§4.3 cross-window). The
  // change may originate from any layer (a user edit or the workspace layer),
  // so re-inspect the key to refresh its override badge too.
  useEffect(() => {
    return client.listen(CONFIG_CHANGED_EVENT, (data) => {
      const change = data as ConfigChange;
      setValues((prev) => ({ ...prev, [change.key]: change.value }));
      refreshInspect(change.key);
    });
  }, [client, refreshInspect]);

  // The shell's own settings render in a section of their own rather than under
  // "Module settings" (见 05 / 23): they come from `PLATFORM_SETTINGS`, not from
  // any manifest, and filing them under a module heading would be a lie about
  // who owns them. Same rows, so reset and the shadowed-layer badge still work.
  //
  // Both lists skip `ownedUi` keys (见 05): their declarer renders them somewhere
  // purpose-built — the Notifications section below for the shell's, a module's own
  // settings page for a module's — and listing them generically too would show the
  // same switch twice.
  const platformSchema = schema.filter(
    (setting) =>
      setting.moduleId === PLATFORM_MODULE_ID && setting.ownedUi !== true,
  );
  const moduleSchema = schema.filter(
    (setting) =>
      setting.moduleId !== PLATFORM_MODULE_ID && setting.ownedUi !== true,
  );

  const update = (key: string, value: unknown): void => {
    setValues((prev) => ({ ...prev, [key]: value }));
    configService.setValue(key, value).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : String(err));
      load();
    });
  };

  const body = (
    <div>
      {error ? (
        <p className="m-0 mb-3 text-[13px] text-destructive">
          {t('workbench.error', { message: error })}
        </p>
      ) : null}

      {autoLaunchBridge ? (
        <SettingsSection
          title={t('settings.general')}
          description={t('settings.general.desc')}
        >
          <GeneralSection bridge={autoLaunchBridge} />
        </SettingsSection>
      ) : null}

      <SettingsSection
        title={t('settings.rail')}
        description={t('settings.rail.desc')}
      >
        <RailLayoutSection launcherService={launcherService} client={client} />
      </SettingsSection>

      <SettingsSection
        title={t('settings.appLinks')}
        description={t('settings.appLinks.desc')}
      >
        <AppLinksSection appLinkService={appLinkService} client={client} />
      </SettingsSection>

      {/* The recent list is user content that can name people and documents,
            so it needs a way out (需求 FR-14 §8 scopes it to the account). The
            host also empties it on a scope change; this is the manual path. */}
      <SettingsSection title={t('workbench.search.section.recent')}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            searchRecentService.clear().catch(() => undefined);
          }}
        >
          {t('workbench.search.clearRecent')}
        </Button>
      </SettingsSection>

      {platformSchema.length > 0 ? (
        <SettingsSection
          title={t('settings.platformSettings')}
          description={t('settings.platformSettings.desc')}
        >
          <div>
            {platformSchema.map((setting) => (
              <SettingRow
                key={setting.key}
                setting={setting}
                value={values[setting.key]}
                inspect={inspects[setting.key]}
                onChange={(value) => update(setting.key, value)}
                onReset={() => {
                  configService.resetValue(setting.key).catch(() => undefined);
                }}
              />
            ))}
          </div>
        </SettingsSection>
      ) : null}

      <SettingsSection title={t('settings.moduleSettings')}>
        {moduleSchema.length === 0 ? (
          <p className="my-2 text-[13px] text-muted-foreground">
            {t('settings.noSettings')}
          </p>
        ) : (
          <div>
            {moduleSchema.map((setting) => (
              <SettingRow
                key={setting.key}
                setting={setting}
                value={values[setting.key]}
                inspect={inspects[setting.key]}
                onChange={(value) => update(setting.key, value)}
                onReset={() => {
                  configService.resetValue(setting.key).catch(() => undefined);
                }}
              />
            ))}
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title={t('settings.permissions')}
        description={t('settings.permissions.desc')}
      >
        <PermissionsSection
          permissionService={permissionService}
          client={client}
        />
      </SettingsSection>

      {osNotificationBridge ? (
        <SettingsSection
          title={t('settings.osNotifications')}
          description={t('settings.osNotifications.desc')}
        >
          <OsNotificationSection bridge={osNotificationBridge} />
        </SettingsSection>
      ) : null}

      {bundleBridge ? (
        <>
          <SettingsSection title={t('settings.modules')}>
            <ModuleInstallSection bridge={bundleBridge} />
          </SettingsSection>
          {bundleBridge.listInstalled ? (
            <SettingsSection title={t('settings.modules.installed.title')}>
              <InstalledModulesSection bridge={bundleBridge} client={client} />
            </SettingsSection>
          ) : null}
        </>
      ) : null}

      {linkedModulesBridge ? (
        <SettingsSection title="Developer">
          <LinkedModulesSection bridge={linkedModulesBridge} />
        </SettingsSection>
      ) : null}

      {logBridge ? (
        <SettingsSection title={t('settings.diagnostics')}>
          <LogsSection bridge={logBridge} />
        </SettingsSection>
      ) : null}

      {devtoolsBridge ? (
        <SettingsSection title="Developer">
          <DeveloperSection bridge={devtoolsBridge} />
        </SettingsSection>
      ) : null}
    </div>
  );
  if (embedded) {
    return body;
  }
  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      title={t('settings.title')}
      size="lg"
    >
      {body}
    </Modal>
  );
}

interface SettingRowProps {
  setting: ResolvedSetting;
  value: unknown;
  inspect?: ConfigInspect;
  onChange: (value: unknown) => void;
  onReset: () => void;
}

function SettingRow({
  setting,
  value,
  inspect,
  onChange,
  onReset,
}: SettingRowProps): ReactNode {
  const { t, title } = useTranslator();
  // The layer this control writes to (where edits land). When a higher-priority
  // layer (e.g. workspace) wins, the user's edit is shadowed — surface that.
  const editScope = setting.scope ?? SCOPE_USER;
  const winner = inspect?.effectiveLayerId;
  const shadowed = winner !== undefined && winner !== editScope;
  return (
    <div className="flex flex-col gap-1.5 border-b border-(--app-color-border-subtle) py-2.5">
      <Label className="text-sm font-semibold">
        {setting.title ? title(setting.moduleId, setting.title) : setting.key}{' '}
        <span className="text-xs font-normal text-(--app-color-text-faint)">
          {setting.key} · {setting.moduleId}
        </span>
        {shadowed ? (
          <span
            title={t('settings.shadowed.title', {
              winner: winner ?? '',
              scope: editScope,
            })}
            className="ml-2 rounded-lg bg-warning px-1.5 py-px text-[11px] font-semibold text-(--app-color-on-accent)"
          >
            {winner}
          </span>
        ) : null}
      </Label>
      <div className="flex items-center gap-2">
        <SettingControl setting={setting} value={value} onChange={onChange} />
        <Button
          variant="outline"
          size="sm"
          onClick={onReset}
          title={t('settings.reset.title')}
        >
          {t('settings.reset')}
        </Button>
      </div>
    </div>
  );
}

interface SettingControlProps {
  setting: ResolvedSetting;
  value: unknown;
  onChange: (value: unknown) => void;
}

function SettingControl({
  setting,
  value,
  onChange,
}: SettingControlProps): ReactNode {
  switch (setting.type) {
    case 'boolean':
      return (
        <Checkbox
          checked={value === true}
          onCheckedChange={(checked) => onChange(checked)}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.valueAsNumber)
          }
          className="flex-1"
        />
      );
    case 'enum':
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          onValueChange={(next) => onChange(next)}
        >
          <SelectTrigger className="flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(setting.enum ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'string':
    default:
      return (
        <Input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange(event.target.value)
          }
          className="flex-1"
        />
      );
  }
}
